import moment from 'moment';
import {ClientZoekMethode} from '../client/clientnummer-selector/clientnummer';
import {Diensten} from '../dienst/diensten';
import UitgevoerdeTaak, {UitgevoerdeTaakDto} from '../prestatie/uitgevoerde-taak';

const dateTimeFormat = 'DD-MM-YYYY HH:mm:ss';
const dateFormat = 'DD-MM-YYYY';

export class ExtendedPrestatieCijfer {

  static deserialize(dto: ExtendedPrestatieCijferDto): ExtendedPrestatieCijfer {
    return new ExtendedPrestatieCijfer(
      dto.id,
      dto.clientId,
      dto.clientNummer,
      dto.clientNaam,
      dto.clientDiensten.map(clientDienstDto => PrestatieCijferClientDienst.deserialize(clientDienstDto)),
      dto.thuisverzorgerId,
      dto.thuisverzorgerNummer,
      dto.thuisverzorgerNaam,
      dto.uitgevoerdeTaken.map(taakDto => UitgevoerdeTaak.deserialize(taakDto)),
      new Date(dto.startTijdstip),
      dto.startMethode,
      dto.stopTijdstip ? new Date(dto.stopTijdstip) : undefined,
      dto.stopMethode
    );
  }

  constructor(private id: string,
              private clientId: string,
              private clientNummer: string,
              private clientNaam: string,
              private clientDiensten: PrestatieCijferClientDienst[],
              private thuisverzorgerId: string,
              private thuisverzorgerNummer: string,
              private thuisverzorgerNaam: string,
              private uitgevoerdeTaken: UitgevoerdeTaak[],
              private startTijdstip: Date,
              private startMethode: ClientZoekMethode,
              private stopTijdstip?: Date,
              private stopMethode?: ClientZoekMethode,
  ) {
  }

  toPrestatieCijfer(): PrestatieCijfer {
    return new PrestatieCijfer(
      this.clientNummer,
      this.clientNaam,
      this.thuisverzorgerNaam,
      this.thuisverzorgerNummer,
      this.getStartAsString(),
      this.startMethode,
      this.getStopAsString(),
      this.stopMethode
    );
  }

  toTaakCijfers(diensten: Diensten): Array<TaakCijfer> {
    return this.uitgevoerdeTaken.map(uitgevoerdeTaak => {
      const dienst = diensten.getDienst(uitgevoerdeTaak.getDienstId());
      const categorie = dienst.getCategorie(uitgevoerdeTaak.getCategorieId());
      const taak = categorie.getTaak(uitgevoerdeTaak.getTaakId());
      const clientZone = this.getClientZone(dienst.getId());
      return new TaakCijfer(
        this.clientNummer,
        this.clientNaam,
        clientZone,
        this.thuisverzorgerNaam,
        this.thuisverzorgerNummer,
        dienst.getLabel(),
        categorie.getLabel(),
        taak.getLabel(),
        this.getTaakCijferDatumAsString(),
      );
    });
  }

  private getStartAsString(): string {
    return moment(this.startTijdstip).format(dateTimeFormat);
  }

  private getStopAsString(): string {
    return !!this.stopTijdstip ? moment(this.stopTijdstip).format(dateTimeFormat) : undefined;
  }

  private getClientZone(dienstId: string) {
    return this.clientDiensten.find(clientDienst => clientDienst.hasDienstId(dienstId))?.getZoneLabel()
      || 'Cliëntregio kan niet bepaald worden omdat client deze dienst niet meer heeft';
  }

  private getTaakCijferDatumAsString(): string {
    return !!this.startTijdstip ? moment(this.startTijdstip).format(dateFormat) : undefined;
  }
}

export class PrestatieCijferClientDienst {
  static deserialize(dto: PrestatieCijferClientDienstDto): PrestatieCijferClientDienst {
    return new PrestatieCijferClientDienst(
      dto.dienstId,
      dto.zoneId,
      dto.zoneLabel,
    );
  }

  constructor(
    private dienstId: string,
    private zoneId: string,
    private zoneLabel: string,
  ) {
  }

  hasDienstId(dienstId: string): boolean {
    return this.dienstId === dienstId;
  }

  getZoneLabel(): string {
    return this.zoneLabel;
  }
}

export class PrestatieCijfer {

  constructor(private clientnummer: string,
              private clientnaam: string,
              private thuisverzorger: string,
              private thuisverzorgernummer: string,
              private start: string,
              private startmethode: string,
              private stop?: string,
              private stopmethode?: string,
  ) {
  }
}

export class TaakCijfer {

  constructor(private clientnummer: string,
              private clientnaam: string,
              private clientregio: string,
              private thuisverzorger: string,
              private thuisverzorgernummer: string,
              private dienst: string,
              private categorie: string,
              private taak: string,
              private datum: string,
  ) {
  }
}

export interface ExtendedPrestatieCijferDto {
  id: string;
  clientId: string;
  clientNummer: string;
  clientNaam: string;
  clientDiensten: Array<PrestatieCijferClientDienstDto>;
  thuisverzorgerId: string;
  thuisverzorgerNaam: string;
  thuisverzorgerNummer: string;
  uitgevoerdeTaken: Array<UitgevoerdeTaakDto>;
  startTijdstip: string;
  startMethode: ClientZoekMethode;
  stopTijdstip?: string;
  stopMethode?: ClientZoekMethode;
}

export interface PrestatieCijferClientDienstDto {
  dienstId: string;
  zoneId: string;
  zoneLabel: string;
}
